/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate } from 'react-router-dom'

import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { faqList, faqMore, FaqRefType } from '@src/configs/faq.config'
import { buildFaqJsonLd } from '@utils'

/**
 * FAQ — Monolith. 문항·근거는 judahsWeb/SITEMAP_PLAN.md §23.
 *
 * Support 허브와 같은 4:8 인덱스 행을 쓰되 행이 링크가 아니다. 왼쪽은 번호와 묶음 이름,
 * 오른쪽은 질문과 답. 아코디언을 쓰지 않는다 — 열 자리가 열 개뿐이고, 접으면 크롤러와
 * AI 답변이 읽는 본문이 첫 화면에서 사라진다(§12.1 평평함).
 *
 * `FAQPage` JSON-LD 를 같이 낸다. 답변 문자열은 `faq.config.ts` 한 곳에서만 나오므로
 * 화면과 구조화 데이터가 갈라지지 않는다.
 */
const MonolithFaqPage = () => {
	// hook
	const navigate = useNavigate()

	const jsonLd = React.useMemo(
		() =>
			buildFaqJsonLd(
				faqList.map(anItem => ({
					question: anItem.question,
					answer: anItem.answer.join(' '),
				})),
			),
		[],
	)

	const go = (href: string) => (e: React.MouseEvent) => {
		if (href.startsWith('mailto:')) {
			return
		}

		e.preventDefault()
		navigate(href)
	}

	const renderRefs = (refs: FaqRefType[]) => (
		<View className="mnFaqRefs">
			{refs.map(aRef => (
				<a key={aRef.href} className="mnFaqRef" href={aRef.href} onClick={go(aRef.href)}>
					{aRef.label}
				</a>
			))}
		</View>
	)

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">FAQ</h1>
					<p className="mnPageLede">
						Orders, shipping and warranty. How to use a product is in its manual.
					</p>
				</View>

				<View className="mnFaq">
					{faqList.map(anItem => (
						<View key={anItem.no} className="mnFaqRow">
							<View className="mnFaqHead">
								<span className="mnFaqNo">{anItem.no}</span>
								<span className="mnFaqGroup">{anItem.group}</span>
							</View>
							<View className="mnFaqBody">
								<h2 className="mnFaqQuestion">{anItem.question}</h2>
								{anItem.answer.map(aParagraph => (
									<p key={aParagraph} className="mnFaqAnswer">
										{aParagraph}
									</p>
								))}
								{anItem.refs !== undefined && renderRefs(anItem.refs)}
							</View>
						</View>
					))}

					<View className="mnFaqRow mnFaqRowMore">
						<View className="mnFaqHead">
							<span className="mnFaqNo" />
							<span className="mnFaqGroup">{faqMore.group}</span>
						</View>
						<View className="mnFaqBody">
							<p className="mnFaqAnswer">{faqMore.body}</p>
							{renderRefs(faqMore.refs)}
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithFaqPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목 전폭, 리드는 그 아래 (DESIGN_SYSTEM §6.2)
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnFaq': {
		borderTop: `1px solid ${mono.line}`,
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	'.mnFaqRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(36px, 3.6vw, 52px) 0',
		borderBottom: `1px solid ${mono.line}`,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 16,
		},
	},

	'.mnFaqHead': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 24,

		'.mnFaqNo': {
			fontSize: 12,
			letterSpacing: '.22em',
			color: mono.faint,
			fontWeight: 500,
			fontVariantNumeric: 'tabular-nums',
			minWidth: 28,
		},
		'.mnFaqGroup': {
			fontSize: 12,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
		},
	},

	'.mnFaqBody': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		paddingRight: 120,
		maxWidth: 620,

		'.mnFaqQuestion': {
			fontSize: 'clamp(20px, 1.7vw, 24px)',
			lineHeight: 1.35,
			fontWeight: 300,
			color: mono.ink,
			marginBottom: 4,
		},
		'.mnFaqAnswer': {
			fontSize: 17,
			lineHeight: 1.75,
			fontWeight: 300,
			color: mono.dim,
		},

		'@media (max-width: 900px)': {
			paddingRight: 0,
		},
	},

	'.mnFaqRefs': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '8px 28px',
		marginTop: 4,

		'.mnFaqRef': {
			fontSize: 13,
			fontWeight: 500,
			letterSpacing: '.02em',
			color: mono.ink,
			textDecoration: 'none',
			transition: 'padding-left 160ms ease-out',

			'&:hover': {
				paddingLeft: 8,
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 2,
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
			},
		},
	},

	// 마감 행. 질문이 아니라 다음 자리를 가리키는 자리라 한 단계 물러선다
	'.mnFaqRowMore': {
		'.mnFaqAnswer': {
			color: mono.faint,
		},
	},
})
